import { useState, useEffect, useMemo } from 'react';
import { Input, Button, Select, SelectItem } from '@heroui/react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useProject, useUpdateProject } from '@/api/projects';
import { useFetchExchangeRate } from '@/api/exchange-rates';
import { useNestingResults } from '@/api/nesting';


interface ProjectSettingsTabProps {
  projectId: string;
}

export default function ProjectSettingsTab({ projectId }: ProjectSettingsTabProps) {
  const { data: project } = useProject(projectId);
  const updateProject = useUpdateProject();
  const fetchRate = useFetchExchangeRate();

  const [rate, setRate] = useState<string>('');
  const [spread, setSpread] = useState<string>('');

  useEffect(() => {
    if (project) {
      setRate(project.mxn_exchange_rate != null ? String(project.mxn_exchange_rate) : '');
      setSpread(String((project as any).mxn_bank_spread ?? 0));
    }
  }, [project]);

  const rateNum = parseFloat(rate) || 0;
  const spreadNum = parseFloat(spread) || 0;
  const effectiveRate = rateNum - spreadNum;
  const isSpreadValid = spreadNum < rateNum || rateNum === 0;

  const fetchedAtLabel = useMemo(() => {
    const ts = (project as any)?.mxn_rate_fetched_at;
    if (!ts) return 'Never';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHrs = Math.round(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs} hr ago`;
    return d.toLocaleDateString();
  }, [project]);

  const handleSaveRate = () => {
    const val = parseFloat(rate);
    if (isNaN(val) || val <= 0) return;
    updateProject.mutate({ id: projectId, mxn_exchange_rate: val } as any);
  };

  const handleSaveSpread = () => {
    const val = parseFloat(spread);
    if (isNaN(val) || val < 0) return;
    if (!isSpreadValid) {
      toast.error('Bank spread must be less than exchange rate');
      return;
    }
    updateProject.mutate({ id: projectId, mxn_bank_spread: val } as any);
  };

  // ── Nesting Cost Overrides ──
  const { data: nestingResults } = useNestingResults(projectId);
  const [costOverrides, setCostOverrides] = useState<Record<string, string>>({});

  useEffect(() => {
    if (project) {
      try {
        setCostOverrides(JSON.parse((project as any).nesting_cost_overrides ?? '{}'));
      } catch { setCostOverrides({}); }
    }
  }, [project]);

  const nestingMaterials = useMemo(() => {
    if (!nestingResults?.materials) return [];
    return Object.values(nestingResults.materials).map((m: any) => ({
      id: m.materialId,
      name: m.materialName,
      defaultMode: m.costMode ?? 'whole_sheet',
    }));
  }, [nestingResults]);

  const handleCostOverrideChange = (materialId: string, mode: string) => {
    const updated = { ...costOverrides };
    if (mode === 'default') {
      delete updated[materialId];
    } else {
      updated[materialId] = mode;
    }
    setCostOverrides(updated);
    updateProject.mutate({ id: projectId, nesting_cost_overrides: JSON.stringify(updated) } as any);
  };

  const handleRefresh = () => {
    fetchRate.mutate(undefined, {
      onSuccess: (data) => {
        setRate(String(data.rate));
        updateProject.mutate({
          id: projectId,
          mxn_exchange_rate: data.rate,
          mxn_rate_fetched_at: data.fetched_at,
        } as any, {
          onSuccess: () => toast.success(`Exchange rate updated: ${data.rate} MXN/USD`),
        });
      },
      onError: () => {
        toast.error('Failed to fetch exchange rate. Enter it manually.');
      },
    });
  };

  return (
    <div className="p-6 max-w-2xl">
      <h2
        className="text-sm font-semibold mb-1"
        style={{ color: 'var(--text-primary)' }}
      >
        Currency Conversion
      </h2>
      <p className="text-xs mb-5" style={{ color: 'var(--text-muted)' }}>
        Configure how Mexican Peso (MXN) materials are converted to USD for this project.
      </p>

      <div
        className="rounded-lg border p-5 space-y-4"
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-surface)',
        }}
      >
        {/* Exchange Rate */}
        <div className="flex gap-3 items-end">
          <Input
            label="Exchange Rate (MXN per USD)"
            type="number"
            value={rate}
            onValueChange={setRate}
            onBlur={handleSaveRate}
            size="sm"
            className="flex-1"
            description={`Last updated: ${fetchedAtLabel}`}
          />
          <Button
            size="sm"
            variant="bordered"
            isIconOnly
            isLoading={fetchRate.isPending}
            onPress={handleRefresh}
            className="border-[var(--border)] text-[var(--text-secondary)] mb-5"
            title="Refresh from market"
          >
            <RefreshCw size={14} />
          </Button>
        </div>

        {/* Bank Spread */}
        <Input
          label="Bank Spread (MXN)"
          type="number"
          value={spread}
          onValueChange={setSpread}
          onBlur={handleSaveSpread}
          size="sm"
          description="Flat amount subtracted from market rate to account for bank fees"
          isInvalid={!isSpreadValid}
          errorMessage={!isSpreadValid ? 'Spread must be less than exchange rate' : undefined}
        />

        {/* Effective Rate */}
        <div
          className="rounded-md px-4 py-3 border"
          style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-base)',
          }}
        >
          <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
            Effective Rate
          </p>
          <p
            className="text-lg font-mono font-semibold"
            style={{ color: effectiveRate > 0 ? 'var(--text-primary)' : 'var(--red)' }}
          >
            {rateNum > 0 ? `${effectiveRate.toFixed(4)} MXN per USD` : 'Not set'}
          </p>
          {rateNum > 0 && effectiveRate > 0 && (
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              1 MXN = ${(1 / effectiveRate).toFixed(6)} USD
            </p>
          )}
        </div>
      </div>

      {/* Nesting Cost Mode Overrides */}
      <h2
        className="text-sm font-semibold mb-1 mt-8"
        style={{ color: 'var(--text-primary)' }}
      >
        Nesting Cost Mode
      </h2>
      <p className="text-xs mb-5" style={{ color: 'var(--text-muted)' }}>
        Override how sheet material costs are calculated for this project. &quot;Whole Sheet&quot; charges
        full sheet price per sheet used. &quot;Used Area&quot; charges proportionally for actual material consumed.
      </p>

      <div
        className="rounded-lg border p-5"
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-surface)',
        }}
      >
        {nestingMaterials.length === 0 ? (
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Run nesting optimization first to see materials here.
          </p>
        ) : (
          <div className="space-y-3">
            {nestingMaterials.map((mat) => (
              <div key={mat.id} className="flex items-center gap-3">
                <span className="text-xs flex-1" style={{ color: 'var(--text-primary)' }}>
                  {mat.name}
                </span>
                <Select
                  selectedKeys={[costOverrides[mat.id] ?? 'default']}
                  onSelectionChange={(keys) => handleCostOverrideChange(mat.id, Array.from(keys)[0] as string)}
                  size="sm"
                  className="w-52"
                >
                  <SelectItem key="default">Material Default ({mat.defaultMode === 'used_area' ? 'Used Area' : 'Whole Sheet'})</SelectItem>
                  <SelectItem key="whole_sheet">Whole Sheet</SelectItem>
                  <SelectItem key="used_area">Used Area Only</SelectItem>
                </Select>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
